const toDate = (value) => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const formatDate = (value, locale) => {
  const date = toDate(value);
  if (!date) {
    return null;
  }
  return new Intl.DateTimeFormat(locale || 'it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const formatNumber = (value, locale, decimals = 2) => {
  if (value == null || value === '') {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return new Intl.NumberFormat(locale || 'it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(numeric);
};

const formatAddress = (address) => {
  if (!address) {
    return null;
  }
  const parts = [
    address.street,
    address.address,
    address.city,
    address.postalCode,
    address.province,
    address.region,
    address.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

const getValueByPath = (obj, path) => {
  if (!obj || !path) {
    return null;
  }
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : null), obj);
};

export const FASCICOLO_FIELD_GROUPS = [
  { id: 'identification', titleKey: 'reports.review.template.fieldGroups.identification' },
  { id: 'management', titleKey: 'reports.review.template.fieldGroups.management' },
  { id: 'codes', titleKey: 'reports.review.template.fieldGroups.codes' },
  { id: 'banking', titleKey: 'reports.review.template.fieldGroups.banking' },
  { id: 'labor', titleKey: 'reports.review.template.fieldGroups.labor' },
];

export const FASCICOLO_FIELD_MAP = [
  {
    id: 'cuaa',
    labelKey: 'reports.review.template.fields.cuaa',
    path: 'cuaa',
    placeholder: 'CUAA',
    required: true,
    groupId: 'identification',
  },
  {
    id: 'legalForm',
    labelKey: 'reports.review.template.fields.legalForm',
    path: 'legalForm',
    placeholder: 'LEGAL_FORM',
    required: true,
    groupId: 'identification',
  },
  {
    id: 'businessName',
    labelKey: 'reports.review.template.fields.businessName',
    path: 'businessName',
    placeholder: 'BUSINESS_NAME',
    required: true,
    groupId: 'identification',
  },
  {
    id: 'officeAddress',
    labelKey: 'reports.review.template.fields.officeAddress',
    path: 'officeAddress',
    format: 'address',
    placeholder: 'REGISTERED_ADDRESS',
    required: true,
    groupId: 'identification',
  },
  {
    id: 'representative',
    labelKey: 'reports.review.template.fields.representative',
    path: 'representative',
    placeholder: 'LEGAL_REPRESENTATIVE',
    required: false,
    groupId: 'identification',
  },
  {
    id: 'taxId',
    labelKey: 'reports.review.template.fields.taxId',
    path: 'taxId',
    placeholder: 'TAX_ID',
    required: false,
    groupId: 'codes',
  },
  {
    id: 'vatNumber',
    labelKey: 'reports.review.template.fields.vatNumber',
    path: 'vatNumber',
    placeholder: 'VAT_NUMBER',
    required: false,
    groupId: 'codes',
  },
  {
    id: 'registryNumber',
    labelKey: 'reports.review.template.fields.registryNumber',
    path: 'registryNumber',
    placeholder: 'REGISTRY_NUMBER',
    required: false,
    groupId: 'codes',
  },
  {
    id: 'inpsCode',
    labelKey: 'reports.review.template.fields.inpsCode',
    path: 'inpsCode',
    placeholder: 'INPS_CODE',
    required: false,
    groupId: 'codes',
  },
  {
    id: 'payingAgency',
    labelKey: 'reports.review.template.fields.payingAgency',
    path: 'payingAgency',
    placeholder: 'PAYING_AGENCY',
    required: true,
    groupId: 'management',
  },
  {
    id: 'openingDate',
    labelKey: 'reports.review.template.fields.openingDate',
    path: 'openingDate',
    format: 'date',
    placeholder: 'OPENING_DATE',
    required: false,
    groupId: 'management',
  },
  {
    id: 'closingDate',
    labelKey: 'reports.review.template.fields.closingDate',
    path: 'closingDate',
    format: 'date',
    placeholder: 'CLOSING_DATE',
    required: false,
    groupId: 'management',
  },
  {
    id: 'holderType',
    labelKey: 'reports.review.template.fields.holderType',
    path: 'holderType',
    placeholder: 'HOLDER_TYPE',
    required: false,
    groupId: 'management',
  },
  {
    id: 'holderCode',
    labelKey: 'reports.review.template.fields.holderCode',
    path: 'holderCode',
    placeholder: 'HOLDER_CODE',
    required: false,
    groupId: 'management',
  },
  {
    id: 'validationDate',
    labelKey: 'reports.review.template.fields.validationDate',
    path: 'validationDate',
    format: 'date',
    placeholder: 'VALIDATION_DATE',
    required: true,
    groupId: 'management',
  },
  {
    id: 'bankAccounts',
    labelKey: 'reports.review.template.fields.bankAccounts',
    path: 'bankAccounts',
    placeholder: 'BANK_ACCOUNTS',
    required: false,
    groupId: 'banking',
  },
  {
    id: 'laborType',
    labelKey: 'reports.review.template.fields.laborType',
    path: 'labor.type',
    placeholder: 'LABOR_TYPE',
    required: false,
    groupId: 'labor',
  },
  {
    id: 'laborUnits',
    labelKey: 'reports.review.template.fields.laborUnits',
    path: 'labor.units',
    placeholder: 'LABOR_UNITS',
    required: false,
    groupId: 'labor',
  },
  {
    id: 'laborHours',
    labelKey: 'reports.review.template.fields.laborHours',
    path: 'labor.hours',
    placeholder: 'LABOR_HOURS',
    required: false,
    groupId: 'labor',
  },
  {
    id: 'farmArea',
    labelKey: 'reports.review.template.fields.farmArea',
    path: 'farmArea',
    format: 'number',
    placeholder: 'FARM_AREA_HA',
    required: false,
    groupId: 'identification',
  },
];

const resolveFieldValue = (field, data, locale) => {
  const raw = getValueByPath(data, field.path);
  if (raw == null || raw === '') {
    return null;
  }
  if (field.format === 'date') {
    return formatDate(raw, locale);
  }
  if (field.format === 'number') {
    return formatNumber(raw, locale);
  }
  if (field.format === 'address') {
    return formatAddress(raw);
  }
  if (Array.isArray(raw)) {
    return raw.length > 0 ? raw.join(', ') : null;
  }
  if (typeof raw === 'object') {
    return JSON.stringify(raw);
  }
  return String(raw);
};

export const mapFascicoloFields = ({ dossier, locale, t, placeholders }) => {
  const fields = FASCICOLO_FIELD_MAP.map((field) => {
    const value = resolveFieldValue(field, dossier, locale);
    const label = t(field.labelKey, { defaultValue: field.id });
    const placeholderLabel = t(field.labelKey, { defaultValue: field.placeholder });
    const fallback = field.required
      ? placeholders.field(field.placeholder || field.id.toUpperCase())
      : placeholders.optional(placeholderLabel);
    return {
      ...field,
      label,
      value: value || fallback,
      isMissing: field.required && !value,
    };
  });

  const groupedFields = FASCICOLO_FIELD_GROUPS.map((group) => {
    const groupFields = fields.filter((field) => field.groupId === group.id);
    return {
      id: group.id,
      title: t(group.titleKey),
      fields: groupFields,
    };
  }).filter(group => group.fields.length > 0);

  const missingFields = fields
    .filter(field => field.isMissing)
    .map(field => ({
      id: field.id,
      label: field.label,
      sectionId: 'fascicolo',
      required: field.required,
    }));

  return {
    fields,
    groupedFields,
    missingFields,
  };
};

export const buildFarmSummary = ({ dossier, locale }) => {
  if (!dossier) {
    return null;
  }
  const totalArea = formatNumber(dossier.totalArea || dossier.landAreaHa, locale);
  return {
    cuaa: dossier.cuaa || null,
    businessName: dossier.businessName || null,
    totalArea,
  };
};
